import type { OnInit } from '@angular/core';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type {
  OnyxCallbackOption,
  OnyxFormMode,
  OnyxTableData,
} from '@onyx/angular';
import {
  ActionHelper,
  OnyxDropdownComponent,
  OnyxSectionComponent,
  OnyxTableComponent,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { differenceBy } from 'lodash';
import { SHORT_PAGINATION } from '../../../../../common/constants/common/short-pagination';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { PointsOfInterestQueryParamKey } from '../../../points-of-interest/common/enums/points-of-interest-query-param-key';
import { PointsOfInterestRoute } from '../../../points-of-interest/common/enums/points-of-interest-route';
import { PointOfInterestHelper } from '../../../points-of-interest/common/helpers/point-of-interest.helper';
import type {
  PointOfInterest,
  SimplifiedPointOfInterest,
} from '../../../points-of-interest/common/interfaces/point-of-interest';
import { CONTRACTORS_POINTS_OF_INTEREST_COLUMNS } from '../../common/constants/contractors-points-of-interest-columns';
import { CONTRACTORS_POINTS_OF_INTEREST_NOT_FOUND } from '../../common/constants/contractors-points-of-interest-not-found';

type ContractorPointsOfInterestFormArray = ReturnType<
  (typeof ContractorPointsOfInterestFormComponent)['buildForm']
>;
export type ContractorPointsOfInterestFormGroup = ReturnType<
  (typeof ContractorPointsOfInterestFormComponent)['buildPointsOfInterestForm']
>;

@Component({
  selector: 'app-contractor-points-of-interest-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxTableComponent,
  ],
  templateUrl: './contractor-points-of-interest-form.component.html',
  styleUrl: './contractor-points-of-interest-form.component.scss',
})
export class ContractorPointsOfInterestFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly router = inject(Router);
  private readonly pointOfInterestHelper = inject(PointOfInterestHelper);

  public readonly form = input.required<ContractorPointsOfInterestFormArray>();
  public readonly mode = input.required<OnyxFormMode>();

  protected readonly I18N = 'contractors.contractorForm.pointsOfInterest';
  protected readonly COLUMNS = CONTRACTORS_POINTS_OF_INTEREST_COLUMNS;
  protected readonly NOT_FOUND = CONTRACTORS_POINTS_OF_INTEREST_NOT_FOUND;
  protected readonly POINTS_OF_INTERES_SOURCE =
    this.pointOfInterestHelper.getPointsOfInterestSource();

  protected readonly DictionaryCode = DictionaryCode;

  protected pointsOfInterest = signal<PointOfInterest[] | null>(null);
  protected pagination = signal(SHORT_PAGINATION);

  protected get pointsOfInterestControl() {
    return this.form().controls.pointsOfInterest;
  }

  public ngOnInit(): void {
    const selectedPointsControl = this.form().controls.selectedPoints;
    selectedPointsControl.setValue(
      this.pointsOfInterestControl
        .getRawValue()
        .map((group) => group.pointOfInterest.uuid),
    );

    effect(
      () => {
        const selectedPoints = this.pointsOfInterest();
        if (
          !selectedPoints?.length &&
          !(selectedPointsControl.touched && selectedPointsControl.dirty)
        ) {
          return;
        }

        const activePoints = this.pointsOfInterestControl
          .getRawValue()
          .map((group) => group.pointOfInterest);

        const removedPoints = differenceBy(
          activePoints,
          selectedPoints ?? [],
          'uuid',
        );
        for (const point of removedPoints) {
          const index = this.pointsOfInterestControl
            .getRawValue()
            .findIndex((group) => group.uuid === point.uuid);
          this.pointsOfInterestControl.removeAt(index);
        }

        const addedPoints = differenceBy(selectedPoints, activePoints, 'uuid');
        for (const point of addedPoints) {
          this.pointsOfInterestControl.push(
            ContractorPointsOfInterestFormComponent.buildPointsOfInterestForm(
              this.fb,
              point,
            ),
          );
        }

        this.pointsOfInterestControl.controls = [
          ...this.pointsOfInterestControl.controls,
        ];
      },
      { injector: this.injector },
    );
  }

  protected addPointOfInterest(name: string | undefined): void {
    const url = this.router
      .createUrlTree([PointsOfInterestRoute.ADD_POINT_OF_INTEREST], {
        queryParams: {
          ...(name && { [PointsOfInterestQueryParamKey.NAME]: name }),
        },
      })
      .toString();
    ActionHelper.openLink(url);
  }

  protected getPointOfInterestOptions = (
    data: OnyxTableData<ContractorPointsOfInterestFormGroup>,
  ): OnyxCallbackOption[] => {
    return [
      {
        name: 'labels.show',
        value: () =>
          this.pointOfInterestHelper.openModal(
            data.item.getRawValue().pointOfInterest,
          ),
      },
    ];
  };

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      selectedPoints: fb.control<string[]>([]),
      pointsOfInterest: fb.array<ContractorPointsOfInterestFormGroup>([]),
    });
  }

  public static buildPointsOfInterestForm(
    fb: NonNullableFormBuilder,
    pointOfInterest: SimplifiedPointOfInterest,
    gate?: string | null,
  ) {
    return fb.group({
      pointOfInterest: fb.control({ value: pointOfInterest, disabled: true }),
      uuid: fb.control({ value: pointOfInterest.uuid, disabled: true }),
      gate: fb.control<string | null>(
        { value: gate ?? null, disabled: false },
        [onyxTextFieldLengthValidator],
      ),
    });
  }
}
